<template>
  <div class="card shadow-sm border-0 p-4 mb-4">
    <!-- Header Title -->
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap">
      <h5 class="fw-bold mb-0 text-primary">
        <i class="bi bi-file-earmark-bar-graph me-2"></i>
        Report Generator
      </h5>
    </div>

    <!-- Date Filters -->
    <div class="row g-3 align-items-center">
      <div class="col-md-6">
        <label class="form-label fw-semibold small text-secondary mb-1">
          <i class="bi bi-calendar3 me-1"></i>{{ $t('start_date') }}
        </label>
        <input
          type="date"
          class="form-control shadow-sm rounded-pill"
          v-model="filters.start_date"
        />
        <div v-if="errors.start_date" class="text-danger small mt-1">
          <i class="bi bi-exclamation-circle"></i> {{ errors.start_date }}
        </div>
      </div>

      <div class="col-md-6">
        <label class="form-label fw-semibold small text-secondary mb-1">
          <i class="bi bi-calendar3 me-1"></i>{{ $t('end_date') }}
        </label>
        <input
          type="date"
          class="form-control shadow-sm rounded-pill"
          v-model="filters.end_date"
        />
        <div v-if="errors.end_date" class="text-danger small mt-1">
          <i class="bi bi-exclamation-circle"></i> {{ errors.end_date }}
        </div>
      </div>
    </div>

    <!-- Divider -->
    <hr class="text-muted my-4" />

    <!-- Buttons -->
    <div class="d-flex justify-content-start gap-3 flex-wrap">
      <button v-if="can('sales.report.sale_return', 'R')" @click="itemReturnReport" class="btn btn-outline-primary rounded-pill px-4">
        <i class="bi bi-arrow-repeat me-2"></i> Sale Return Report
      </button>

      <button v-if="can('inventory.report.stock', 'R')" @click="itemStockReport" class="btn btn-outline-primary rounded-pill px-4">
        <i class="bi bi-box-seam me-2"></i> Item Stock Report
      </button>

      <button v-if="can('purchase.report.batch', 'R')" @click="invBatchReport" class="btn btn-outline-primary rounded-pill px-4">
        <i class="bi bi-box-seam me-2"></i> Inventory Batch Report
      </button>

      <button v-if="can('sales.report.sale', 'R')" @click="invSellReport" class="btn btn-outline-primary rounded-pill px-4">
        <i class="bi bi-box-seam me-2"></i> Inventory Sell Report
      </button>

      <button v-if="can('purchase.report.buy', 'R')" @click="invBuyReport" class="btn btn-outline-primary rounded-pill px-4">
        <i class="bi bi-box-seam me-2"></i> Inventory Buy Report
      </button>

      <button v-if="can('inventory.report.low_stock', 'R')" @click="invLowStockReport" class="btn btn-outline-primary rounded-pill px-4">
        <i class="bi bi-box-seam me-2"></i> Inventory Low Stock Report
      </button>

      <button v-if="can('sales.report.customer_due', 'R')" @click="customersDueReport" class="btn btn-outline-primary rounded-pill px-4">
        <i class="bi bi-box-seam me-2"></i> Customers Due Report
      </button>

      <button v-if="can('purchase.report.supplier_payment', 'R')" @click="suppliersPaymentReport" class="btn btn-outline-primary rounded-pill px-4">
        <i class="bi bi-box-seam me-2"></i> Suppliers Payment Report
      </button>
    </div>
  </div>
</template>


<script>
import apiUrls from "@/urls";
import { useToast } from 'vue-toastification';

export default {
  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      filters: {
        start_date: "",
        end_date: "",
        item_code: "",
      },
      errors: {}
    };
  },

  methods: {
    can(code, requiredLevel) {
      const perms = JSON.parse(localStorage.getItem("permissions")) || {};
      const userLevel = perms[code] || 'N';

      for (const char of requiredLevel) {
        if (!userLevel.includes(char)) {
          return false;
        }
      }
      return true;
    },

    async itemReturnReport() {
      try {
        const params = new URLSearchParams(this.filters).toString();

        const response = await this.$axios.get(`${apiUrls.item_return_report.get}?${params}`, {responseType: 'blob'});

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = 'return_item_report.pdf';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.toast.success("Item return report successfully downloaded!");
        this.$emit("create_success");
        this.resetForm()

      } catch (error) {
          console.log(error)
        }
      },

    async itemStockReport() {
      try {
        const params = new URLSearchParams(this.filters).toString();

        const response = await this.$axios.get(`${apiUrls.item_stock_report.get}?${params}`, {responseType: 'blob'});

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = 'item_stock_report.pdf';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.toast.success("Item Stock report successfully downloaded!");
        this.$emit("create_success");
        this.resetForm()

      } catch (error) {
          console.log(error)
        }
      },

    async invBatchReport() {
      try {
        const params = new URLSearchParams(this.filters).toString();

        const response = await this.$axios.get(`${apiUrls.inventory_batch_report.get}?${params}`, {responseType: 'blob'});

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = 'inventory_batch_report.pdf';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.toast.success("Inventory Batch report successfully downloaded!");
        this.$emit("create_success");
        this.resetForm()

      } catch (error) {
          console.log(error)
        }
      },

    async invSellReport() {
      try {
        const params = new URLSearchParams(this.filters).toString();

        const response = await this.$axios.get(`${apiUrls.inventory_sell_report.get}?${params}`, {responseType: 'blob'});

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = 'inventory_sell_report.pdf';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.toast.success("Inventory Sell report successfully downloaded!");
        this.$emit("create_success");
        this.resetForm()

      } catch (error) {
          console.log(error)
        }
      },

    async invBuyReport() {
      try {
        const params = new URLSearchParams(this.filters).toString();

        const response = await this.$axios.get(`${apiUrls.inventory_buy_report.get}?${params}`, {responseType: 'blob'});

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = 'inventory_buy_report.pdf';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.toast.success("Inventory Buy report successfully downloaded!");
        this.$emit("create_success");
        this.resetForm()

      } catch (error) {
          console.log(error)
        }
      },

    async invLowStockReport() {
      try {
        const params = new URLSearchParams(this.filters).toString();

        const response = await this.$axios.get(`${apiUrls.inventory_low_stock_report.get}?${params}`, {responseType: 'blob'});

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = 'inventory_low_stock_report.pdf';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.toast.success("Inventory Low Stock report successfully downloaded!");
        this.$emit("create_success");
        this.resetForm()

      } catch (error) {
          console.log(error)
        }
      },

    async customersDueReport() {
      try {
        const params = new URLSearchParams(this.filters).toString();

        const response = await this.$axios.get(`${apiUrls.customers_due_report.get}?${params}`, {responseType: 'blob'});

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = 'customers_due_report.pdf';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.toast.success("Customers Due report successfully downloaded!");
        this.$emit("create_success");
        this.resetForm()

      } catch (error) {
          console.log(error)
        }
      },

    async suppliersPaymentReport() {
      try {
        const params = new URLSearchParams(this.filters).toString();

        const response = await this.$axios.get(`${apiUrls.suppliers_payment_report.get}?${params}`, {responseType: 'blob'});

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = 'suppliers_payment_report.pdf';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.toast.success("Suppliers Payment report successfully downloaded!");
        this.$emit("create_success");
        this.resetForm()

      } catch (error) {
          console.log(error)
        }
      },

    resetForm() {
      this.filters = {
        start_date: "",
        end_date: "",
        item_code: "",
      };
      this.errors = {};
    },
  }
}

</script>